import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('galeries_token'));
    const [loading, setLoading] = useState(true);

    const api = useCallback(() => {
        const instance = axios.create({
            baseURL: API,
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        return instance;
    }, [token]);

    const refreshUser = useCallback(async () => {
        if (token) {
            try {
                const response = await api().get('/auth/me');
                setUser(response.data);
                return response.data;
            } catch (error) {
                console.error('Failed to refresh user:', error);
                return null;
            }
        }
        return null;
    }, [token, api]);

    useEffect(() => {
        const checkAuth = async () => {
            if (token) {
                try {
                    const response = await api().get('/auth/me');
                    setUser(response.data);
                } catch (error) {
                    console.error('Auth check failed:', error);
                    localStorage.removeItem('galeries_token');
                    setToken(null);
                    setUser(null);
                }
            }
            setLoading(false);
        };
        checkAuth();
    }, [token, api]);

    const sendOTP = async (phone) => {
        const response = await api().post('/auth/send-otp', { phone });
        return response.data;
    };

    const verifyOTP = async (phone, code, name = null, inviteCode = null) => {
        const response = await api().post('/auth/verify-otp', {
            phone,
            code,
            name,
            invite_code: inviteCode,
        });
        const { token: newToken, user: userData } = response.data;
        
        // Only auto-login if user has already accepted consent
        if (userData.consent_accepted === true) {
            localStorage.setItem('galeries_token', newToken);
            setToken(newToken);
            setUser(userData);
        }
        
        return response.data;
    };

    const completeLogin = (newToken, userData) => {
        // Call this after consent is accepted
        localStorage.setItem('galeries_token', newToken);
        setToken(newToken);
        setUser(userData);
    };

    const logout = async () => {
        try {
            await api().post('/auth/logout');
        } catch (error) {
            console.error('Logout error:', error);
        }
        localStorage.removeItem('galeries_token');
        setToken(null);
        setUser(null);
    };

    const value = {
        user,
        token,
        loading,
        isAuthenticated: !!user,
        isAdmin: user?.is_admin || false,
        sendOTP,
        verifyOTP,
        completeLogin,
        logout,
        api,
        refreshUser,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
